/**
 * Project Onboarding service — the internal team kickoff checklist that gets a newly-executed
 * project fully set up in BQE Core. Backed by the Airtable `Project Onboarding` table
 * (tblB7HF79Kc9ObOMp) — the record itself is created by the execution-time Airtable automation
 * (the same one that assigns the project number and Drive folder); this app only ever reads and
 * patches it, never creates it. Ported from the legacy Softr wizard in
 * examples/opportunity_details.html — see docs/PROJECT_ONBOARDING.md for the full logic record.
 *
 * Field-format contracts (must stay byte-compatible with legacy records):
 *   - 'Existing Client' / 'DC Retainer Required' / 'BS Retainer Required' are singleSelects
 *     storing the strings "Yes"/"No" (NOT booleans).
 *   - Step flags ('Ready for Core Phases', 'Add Client to BQE Core', 'Add Project to BQE Core',
 *     'BS Ready for Core', 'BS Phases Verified', 'DC/BS Send Retainer Request',
 *     'Onboarding Complete', 'BS Onboarding Complete', 'BS Onboarding Active') are checkboxes.
 *   - Per-section sub-checklists persist as JSON blobs in the '* Section State' text fields.
 */
import { apiGet, apiGetOne, apiPatch } from './_api.js'
import { getProjectDetails, updateProjectDetails } from './projects.js'
import { getOrganizationDetail } from './contacts.js'
import { logInteraction } from './interactions.js'

const TBL_ONB = 'tblB7HF79Kc9ObOMp'
const TBL_CONTACTS = 'tblEcywoAVDwkOLiK'

// Fields that only ever get set once real onboarding progress has happened — used to pick the
// right record if a project ever ends up with more than one (see below). Order doesn't matter,
// this is just a presence count.
const ONBOARDING_PROGRESS_FIELDS = [
  'Ready for Core Phases', 'Ready for Core', 'BS Ready for Core', 'Add Client to BQE Core',
  'Add Contact to Client', 'Add Project to BQE Core', 'BS Phases Verified',
  'DC Send Retainer Request', 'BS Send Retainer Request', 'Onboarding Complete',
  'BS Onboarding Complete', 'Client Section State', 'Project Section State',
  'BS Project Section State', 'Retainer Section State',
]
function onboardingProgressScore(rec) {
  const f = rec.fields || {}
  return ONBOARDING_PROGRESS_FIELDS.reduce((n, key) => n + (f[key] ? 1 : 0), 0)
}

/** The onboarding record for a project, or null. Primary match is the Project Record UID lookup;
 *  falls back to the linked-record ARRAYJOIN filter (both ported from the legacy loader).
 *
 *  A project should only ever have one onboarding record, but a mistimed automation re-fire can
 *  create a second, empty one (confirmed on Grabowski Residence Remodel, 2026-07 — see
 *  docs/PROJECT_ONBOARDING.md). Airtable's default record order isn't guaranteed to put the real
 *  one first, so if more than one match comes back, prefer whichever actually has progress on it
 *  instead of picking arbitrarily. */
export async function getOnboardingForProject(projectId) {
  let recs = await apiGet(TBL_ONB, 'filterByFormula=' +
    encodeURIComponent(`{Project Record UID (from Project)}='${projectId}'`))
  if (!recs || !recs.length) {
    recs = await apiGet(TBL_ONB, 'filterByFormula=' +
      encodeURIComponent(`FIND('${projectId}',ARRAYJOIN({Project},','))`))
  }
  if (!recs || !recs.length) return null
  if (recs.length === 1) return recs[0]
  return recs.reduce((best, rec) => (onboardingProgressScore(rec) > onboardingProgressScore(best) ? rec : best), recs[0])
}

/**
 * Everything the onboarding wizard needs in one call: the onboarding record, the full project
 * record, the client Organization, and its Primary Contact. org/contact are best-effort (null on
 * failure) — they only feed display rows, never gating logic.
 */
export async function getOnboardingBundle(projectId) {
  const [onboarding, project] = await Promise.all([
    getOnboardingForProject(projectId),
    getProjectDetails(projectId),
  ])
  let org = null, contact = null
  const clientId = project && Array.isArray(project.fields['Client']) ? project.fields['Client'][0] : null
  if (clientId) {
    try { org = await getOrganizationDetail(clientId) } catch (_) { /* display-only */ }
    const primaryId = org && Array.isArray(org.fields['Primary Contact']) ? org.fields['Primary Contact'][0] : null
    if (primaryId) {
      try { contact = await apiGetOne(TBL_CONTACTS, primaryId) } catch (_) { /* display-only */ }
    }
  }
  return { onboarding, project, org, contact }
}

/** Patch fields on the onboarding record. */
export function updateOnboarding(onbId, fields) {
  return apiPatch(TBL_ONB, onbId, fields, { typecast: true })
}

/**
 * Admin-only backfill path: set/replace a project's Client Manager after the fact. The onboarding
 * wizard's "locked after Confirm Setup" rule (OnboardingLauncher.jsx) only lives in that
 * component's React state — the Airtable field itself isn't schema-locked — so this is a
 * legitimate, separate edit path for the projects that never got one (see docs/PROGRESS_GATE.md's
 * Client Manager coverage gap). `memberId` falsy clears it.
 */
export async function setClientManager(projectId, memberId) {
  const onb = await getOnboardingForProject(projectId)
  if (!onb) throw new Error('No onboarding record found for this project')
  return updateOnboarding(onb.id, { 'Client Manager': memberId ? [memberId] : [] })
}

/**
 * Push a newly-assigned Client Manager onto the matching BQE Core project + all its phases
 * (server-side — api/sync-client-manager.js; BQE writes never happen from the browser). Call this
 * AFTER setClientManager() succeeds on the Airtable side — this only handles the Core half.
 * Best-effort: BQE's project-update API has a documented history of being flaky on this tenant, so
 * a partial/failed result here doesn't mean the Client Manager assignment itself failed. Returns
 * `{ok:false, skipped:true, reason}` (not an error) when the member has no BQE Contact ID or the
 * project isn't yet scaffolded into Core — those are expected, not failures.
 * @returns {Promise<{ok:boolean, results?:Array, failedCount?:number, skipped?:boolean, reason?:string, error?:string}>}
 */
export async function syncClientManagerToCore(projectId, memberId) {
  let token = null
  try { token = typeof window.__clerkGetToken === 'function' ? await window.__clerkGetToken().catch(() => null) : null } catch (_) {}
  const res = await fetch('/api/sync-client-manager', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: 'Bearer ' + token } : {}) },
    body: JSON.stringify({ projectId, memberId }),
  })
  return res.json().catch(() => ({ ok: false, error: 'Request failed: ' + res.status }))
}

/** Stamp the Project's acknowledged flag — set by the "Begin Onboarding" overlay CTA (first
 *  cycle) so the full-screen prompt only interrupts once per project, not per person per visit. */
export function acknowledgeOnboarding(projectId, { bs = false } = {}) {
  return updateProjectDetails(projectId, { [bs ? 'BS Onboarding Acknowledged' : 'Onboarding Acknowledged']: true })
}

/**
 * Final completion — ports the legacy makeActionBtn completion branch exactly:
 * sets the cycle's Complete checkbox, stamps the Project's acknowledged flag, logs the
 * Interaction. `cycle` is 'dc' (DC-track first cycle → 'Onboarding Complete'), or 'bs'
 * (BS-direct first cycle AND the DC→BS second cycle → 'BS Onboarding Complete').
 * The second cycle additionally clears 'BS Onboarding Active'.
 */
export async function completeOnboarding({ onbId, projectId, cycle, secondCycle = false, memberId }) {
  const onbFields = cycle === 'dc' ? { 'Onboarding Complete': true } : { 'BS Onboarding Complete': true }
  if (secondCycle) onbFields['BS Onboarding Active'] = false
  await apiPatch(TBL_ONB, onbId, onbFields, { typecast: true })
  await updateProjectDetails(projectId, { [cycle === 'dc' ? 'Onboarding Acknowledged' : 'BS Onboarding Acknowledged']: true })
  const notes = cycle === 'dc'
    ? 'DC onboarding completed. Project is ready to advance to Basic Services.'
    : 'Onboarding completed. Project is now in production.'
  await logInteraction({ type: 'Action Taken', projectId, notes, teamMemberId: memberId || undefined }).catch(() => {})
}

/**
 * Run the Core scaffold (api/scaffold-project.js): creates the BQE client + contacts when the
 * org has no BQE Core ID yet, the Core parent project, all phases (Airtable + Core), and the
 * WBS milestone scaffolding. Clerk-authed (the legacy x-aj-token secret no longer ships to the
 * browser — the endpoint accepts either, via api/_timeAuth.js). Returns the endpoint's
 * { success, projectCode, phasesCreated, milestonesCreated, log } payload; throws on failure
 * with the server's error message.
 */
export async function scaffoldProject(projectId) {
  let token = null
  try { token = typeof window.__clerkGetToken === 'function' ? await window.__clerkGetToken().catch(() => null) : null } catch (_) {}
  const res = await fetch('/api/scaffold-project', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: 'Bearer ' + token } : {}) },
    body: JSON.stringify({ projectRecordId: projectId }),
  })
  const d = await res.json().catch(() => ({}))
  if (!res.ok || !d.success) throw new Error(d.error || 'Core scaffold failed')
  return d
}
